/**
 * Engine.ts
 * Babylon.js scene setup for the Dungeon Master editor.
 * Arc-rotate camera, ambient + directional lighting, render loop.
 */

import {
    Engine as BabylonEngine,
    Scene,
    ArcRotateCamera,
    HemisphericLight,
    DirectionalLight,
    Vector3,
    Color3,
    Color4,
    PointerEventTypes,
    type PointerInfo
} from '@babylonjs/core'

export interface PointerCallbacks {
    onDown?: (info: PointerInfo) => void
    onMove?: (info: PointerInfo) => void
    onUp?: (info: PointerInfo) => void
}

export class Engine {
    readonly babylon: BabylonEngine
    readonly scene: Scene
    readonly camera: ArcRotateCamera
    private pointerCallbacks: PointerCallbacks = {}

    constructor(canvas: HTMLCanvasElement) {
        this.babylon = new BabylonEngine(canvas, true, {
            preserveDrawingBuffer: true,
            stencil: true,
            antialias: true
        })

        this.scene = new Scene(this.babylon)
        this.scene.clearColor = new Color4(0.04, 0.04, 0.05, 1.0)

        // Camera: isometric-style orbit
        this.camera = new ArcRotateCamera(
            'camera',
            -Math.PI / 4,       // alpha (horizontal)
            Math.PI / 3.5,      // beta (elevation)
            120,                 // radius
            Vector3.Zero(),
            this.scene
        )
        this.camera.lowerRadiusLimit = 20
        this.camera.upperRadiusLimit = 300
        this.camera.wheelPrecision = 5
        this.camera.panningSensibility = 50
        this.camera.attachControl(canvas, true)

        this.setupLighting()
        this.setupPointer()

        window.addEventListener('resize', () => this.babylon.resize())
        this.babylon.runRenderLoop(() => this.scene.render())
    }

    private setupLighting(): void {
        const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene)
        hemi.intensity = 0.5
        hemi.diffuse = new Color3(0.75, 0.78, 0.85)
        hemi.groundColor = new Color3(0.12, 0.1, 0.08)

        const dir = new DirectionalLight('dir', new Vector3(-1, -2, -1).normalize(), this.scene)
        dir.intensity = 0.5
        dir.diffuse = new Color3(1, 0.95, 0.85)
    }

    private setupPointer(): void {
        this.scene.onPointerObservable.add((info: PointerInfo) => {
            switch (info.type) {
                case PointerEventTypes.POINTERDOWN:
                    this.pointerCallbacks.onDown?.(info)
                    break
                case PointerEventTypes.POINTERMOVE:
                    this.pointerCallbacks.onMove?.(info)
                    break
                case PointerEventTypes.POINTERUP:
                    this.pointerCallbacks.onUp?.(info)
                    break
            }
        })
    }

    setPointerCallbacks(callbacks: PointerCallbacks): void {
        this.pointerCallbacks = callbacks
    }

    focusOn(target: Vector3, radius?: number): void {
        this.camera.target = target
        if (radius !== undefined) this.camera.radius = radius
    }

    /** Top-down view for grid editing */
    setTopDown(): void {
        this.camera.alpha = 0
        this.camera.beta = 0.05
    }

    /** 3/4 perspective view */
    setPerspective(): void {
        this.camera.alpha = -Math.PI / 4
        this.camera.beta = Math.PI / 3.5
    }

    dispose(): void {
        this.babylon.stopRenderLoop()
        this.scene.dispose()
        this.babylon.dispose()
    }
}
